import {APP_BASE_HREF, DOCUMENT, isPlatformBrowser, registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi} from '@angular/common/http';
import en from '@angular/common/locales/en';
import ru from '@angular/common/locales/ru';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  Injector,
  LOCALE_ID,
  PLATFORM_ID,
  signal,
  TransferState,
} from '@angular/core';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideClientHydration, withEventReplay, withHttpTransferCacheOptions} from '@angular/platform-browser';
import {provideRouter} from '@angular/router';
import {provideServiceWorker} from '@angular/service-worker';
import {NbIconLibraries, NbToastrModule} from '@nebular/theme';
import {LOADING_BAR_CONFIG} from '@ngx-loading-bar/core';
import {LoadingBarHttpClientModule} from '@ngx-loading-bar/http-client';
import {LoadingBarRouterModule} from '@ngx-loading-bar/router';
import {TUI_SCROLLBAR_OPTIONS, provideTaiga} from '@taiga-ui/core';
import {TUI_LANGUAGE} from '@taiga-ui/i18n';
import {AuthModule, AuthService, HOME_ROUTE, MENU_ACCESS_URL_NORMALIZER, SIGN_IN_ROUTE} from 'compob2b-auth';
import {CompoErrorHandler, CompoIconLibraries, Environment, setSupportedLocales, StoreService} from 'compob2b-libs';
import {NgxPaginationModule} from 'ngx-pagination';
import {environment} from '../environments/environment';
import {toLegacyUrl} from './cabinet/shared/routing/legacy-url-map';
import {CabinetModule} from './cabinet/cabinet.module';
import {resolveIsShowcase} from './cabinet/shared/tokens/is-showcase.factory';
import {IS_SHOWCASE} from './cabinet/shared/tokens/is-showcase.token';
import {initApp, loadRoutes} from './COMPO_APP_INITIALIZER';
import {ShowcaseContextService} from './cabinet/utils/showcase-context.service';
import {ShowcaseHashInterceptor} from './cabinet/utils/showcase-hash.interceptor';
import {AcceptLanguageInterceptor} from './cabinet/shared/locale/accept-language.interceptor';
import {routes} from './app.routes';
import {SEO_CONFIG, seoConfigFactory} from './cabinet/shared/seo/seo-config.token';
import {SeoConfigClient} from './cabinet/shared/seo/seo-config.client';

registerLocaleData(ru);
registerLocaleData(en);

export const appConfig: ApplicationConfig = {
  providers: [
    // Root-провайдеры из NgModule-импортов, у которых нет provide*-аналога:
    // CabinetModule агрегирует все .forRoot() (NbTheme/Menu/Sidebar/Dialog/Window/
    // Toastr-нет, Formly, Shared) и сам ничего не declares — переносится целиком.
    importProvidersFrom(
      AuthModule.forRoot(environment),
      CabinetModule,
      LoadingBarHttpClientModule,
      LoadingBarRouterModule,
      NgxPaginationModule,
      NbToastrModule.forRoot(),
    ),
    provideRouter(routes),
    provideAnimations(),
    provideServiceWorker('ngsw-worker.js', {
      // SSR-safe через ngsw-config.json `navigationUrls: []`: SW не перехватывает
      // route-URL'ы (иначе отдавал бы кэш-shell вместо серверного рендера),
      // кэширует только статику → детект новой версии (SwUpdate) работает на
      // всех доменах, включая SSR-домен.
      enabled: environment.production && environment.serviceWorkers,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),

    // Политика проверки доступа к разделу (SEO-8). `AuthGuardMenuService` живёт в
    // общей библиотеке и используется ТАКЖЕ админкой, поэтому карту URL кабинета
    // задаёт приложение, а не сама библиотека (иначе повторили бы ошибку §11.1 со
    // `StoreService`). Бэк матчит `strpos(:url, menu.uri) > 0`, а `menu.uri` остаётся
    // в legacy-форме — без этого главная недоступна: strpos('/', '/cabinet/dashboard') = 0.
    // Именно toLegacyUrl, а НЕ toLegacyPath: гард передаёт `state.url` с query.
    {provide: MENU_ACCESS_URL_NORMALIZER, useValue: toLegacyUrl},
    {provide: SIGN_IN_ROUTE, useValue: ['/sign-in']},
    // Главная кабинета после переезда — корень сайта, а не `/cabinet/dashboard`
    // (гарды общей библиотеки уводят сюда гостя с формы входа и не-покупателя).
    {provide: HOME_ROUTE, useValue: '/'},
    {provide: ErrorHandler, useClass: CompoErrorHandler},
    {provide: LOADING_BAR_CONFIG, useValue: {latencyThreshold: 200}},
    // Состав контент-локалей — из environment (браузер И SSR: server-config мержит этот appConfig).
    {provide: APP_INITIALIZER, multi: true, useFactory: () => () => setSupportedLocales(environment.contentLocales)},
    // init + SEO-конфиг одним провайдером: в браузере второй зависит от anon-токена,
    // который добывает первый (см. initApp).
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AuthService, PLATFORM_ID, StoreService, TransferState, SeoConfigClient],
      useFactory: initApp,
    },
    {provide: APP_INITIALIZER, multi: true, deps: [Injector], useFactory: loadRoutes},
    // Регистрация SVG-пака иконок (было в конструкторе AppModule).
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [NbIconLibraries],
      useFactory: (iconLibraries: NbIconLibraries) => () => {
        iconLibraries.registerSvgPack('compo', {iconClassPrefix: 'co'});
        iconLibraries.setDefaultPack('compo');
      },
    },
    {provide: SEO_CONFIG, useFactory: seoConfigFactory, deps: [TransferState]},
    {provide: LOCALE_ID, useValue: environment.locale},
    {provide: TUI_LANGUAGE, useValue: signal(environment.tuiLanguage)},
    {provide: TUI_SCROLLBAR_OPTIONS, useValue: {mode: 'native'}},
    {provide: 'environment', useValue: environment},
    {provide: Environment, useValue: environment},
    {provide: NbIconLibraries, useClass: CompoIconLibraries},
    {
      provide: APP_BASE_HREF, useFactory: () => {
        const hash = ShowcaseContextService.readHash();
        return hash ? `/${hash}/` : '/';
      },
    },
    {provide: HTTP_INTERCEPTORS, useClass: ShowcaseHashInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AcceptLanguageInterceptor, multi: true},
    // scrollbars: 'native' — ОБЯЗАТЕЛЬНО в паре с TUI_SCROLLBAR_OPTIONS выше.
    // Taiga 5 разносит одно решение по двум токенам: TUI_SCROLLBAR_OPTIONS.mode гасит
    // рендер <tui-scroll-controls>, а класс `tui-zero-scrollbar` на <html>
    // (scrollbar-width: none + ::-webkit-scrollbar{display:none}) вешает
    // app-initializer из provideTaiga() по TUI_OPTIONS.scrollbars, дефолт которого
    // 'custom'. Без этого аргумента нативная полоса погашена, а кастомная не
    // нарисована — страница скроллится колесом, но скроллбара не видно вообще.
    provideTaiga({scrollbars: 'native'}),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
        filter: (req) => {
          // Anonymous-accessible read endpoints. Match с path-segment boundary
          // (`/news` ИЛИ `/news/...`), а не голым startsWith — иначе `/news-internal`,
          // `/pages-admin`, `/menus-private` тихо opt-in'ятся в TransferState и
          // authenticated body может утечь в SSR HTML.
          // Whitelist endpoints, ответы которых customer-group-INVARIANT —
          // т.е. SSR-рендер под анон-токеном даёт ту же data, что и client
          // под logged-in B2B-токеном. Если backend варьирует response по
          // customer-group, TransferState приведёт к price-leak: logged-in
          // юзер видит anon-default цены на первой навигации после SSR.
          // `/product` целиком запрещён — `/product/{uid}` отдаёт price
          // зависящий от group. Конкретные anon-safe sub-paths включены явно.
          const allowedPrefixes = [
            '/init',                          // site-wide settings (siteName, address)
            '/menu',                          // анонимное меню
            '/catalog',                       // catalog/tree, catalog/parent-chain — иерархия group-invariant
            '/news',
            '/pages',                         // /pages/{id} — text pages, /pages/links/{id} — page-link tree
            '/ad-campaigns',
            '/promotions',
            '/sales-points',
            '/banner',                        // главный баннер — group-invariant
            '/customer-group/default',        // дефолтная группа для анона — статика
            '/seo/list-view',                 // подборки: header/title/filterQuery, цен нет → group-invariant
          ];
          // Protocol-relative URL (`//evil/...`) под `new URL` подменяет origin
          // на attacker — pathname становится attacker'овым path'ом. Раннее reject.
          if (req.url.startsWith('//')) {
            return false;
          }
          let path: string;
          try {
            // Base URL — заглушка для относительных URL; абсолютные парсятся как есть.
            path = new URL(req.url, 'http://x').pathname;
          } catch {
            return false;
          }
          return allowedPrefixes.some(prefix => path === prefix || path.startsWith(prefix + '/'));
        },
        includeHeaders: [],
        includePostRequests: false,
      }),
    ),
    {
      provide: IS_SHOWCASE,
      useFactory: (platformId: object, doc: Document) => {
        // CLAUDE.md SSR-policy: не обращаемся к `window` напрямую и не
        // сравниваем `window.location.host` raw'ом. На SSR isPlatformBrowser
        // возвращает false → cabinet. В browser'е достаём host через
        // DOCUMENT.defaultView (canonical Angular DI вместо global'а).
        if (!isPlatformBrowser(platformId)) return false;
        const host = doc.defaultView?.location.host;
        return resolveIsShowcase(host, environment.showcaseUrl);
      },
      deps: [PLATFORM_ID, DOCUMENT],
    },
  ],
};
